import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';
import { RemoteButton, useRemote } from '../hooks/useRemote';
import { ReactPlayerProps } from 'react-player';
import { useCitrusViewInformation } from '../hooks/useCitrusViewInformation';
import { useUser } from './UserContext';
import { useFetch } from '../hooks/useFetch';
import { useOperator } from './OperatorContext';
import { mapAccount } from '../hooks/Account/AccountHelper';
import { HosApiAccount } from '../types/apiTypes';
import { BASE_URL_MAP } from '../lib/environment';

interface CitrusViewProviderProps {
  children: ReactNode;
}

export interface Remote {
  buttons: RemoteButton[];
  hidden: boolean;
}

export interface VideoPlayerStatistics {
  bandwidth: number;
  droppedFrames: number;
  latency: number;
}

interface CitrusViewContext {
  citrusViewDevice?: Device;
  connect(): void;
  connected: boolean;
  connectedUser: string | undefined;
  devices: Device[];
  disconnect(): void;
  hasCitrusView: boolean;
  hideRemote(hidden: boolean): void;
  isMobile: boolean;
  jumpToLive: boolean;
  loading: boolean;
  onVideoDrift(jumpToLive: boolean): void;
  onConnected(playerEl: HTMLVideoElement): void;
  playerConfig: ReactPlayerProps;
  playerEl: HTMLVideoElement | null;
  reboot(): void;
  rebooting: boolean;
  remote: Remote | null;
  remoteLoading: boolean;
  resetSource(): void;
  sendCommand(buttonId: number): void;
  setCitrusViewDevice(device?: Device): void;
  source: string | undefined;
  statistics: VideoPlayerStatistics;
  updatePlayerConfig(
    config: keyof ReactPlayerProps | ReactPlayerProps,
    value?: any
  ): void;
  updateStatistics(
    statistic: keyof VideoPlayerStatistics | VideoPlayerStatistics,
    value?: any
  ): void;
}

const CitrusViewContext = createContext<CitrusViewContext | undefined>(
  undefined
);

export const CitrusViewProvider = ({ children }: CitrusViewProviderProps) => {
  const [connected, setConnected] = useState(false);
  const [source, setSource] = useState<string>();
  const [jumpToLive, setJumpToLive] = useState(false);
  const [playerEl, setPlayerEl] = useState<HTMLVideoElement | null>(null);
  const [statistics, setStatistics] = useState<VideoPlayerStatistics>({
    bandwidth: 0,
    latency: 0,
    droppedFrames: 0,
  });
  const [remote, setRemote] = useState<Remote | null>(null);
  const [playerConfig, setPlayerConfig] = useState<ReactPlayerProps>({
    controls: true,
    light: false,
    muted: false,
    pip: true,
    playing: true,
    volume: 0.2,
  });
  const [citrusViewDevice, setCitrusViewDevice] = useState<
    Device | undefined
  >();
  const isMobile = window.outerWidth < 900; //&& navigator.maxTouchPoints > 1;

  const {
    buttons,
    getRemoteButtons,
    loading: remoteLoading,
    sendCommand,
  } = useRemote();

  const { connect, disconnect, info, loading, reboot, rebooting } =
    useCitrusViewInformation();
  const { email } = useUser();
  const { devices } = useCitrusViewAccount();

  useEffect(() => {
    if (devices.length === 1) setCitrusViewDevice(devices[0]);
    else setCitrusViewDevice(undefined);
  }, [devices]);

  useEffect(() => {
    if (!remoteLoading && info?.currentUser !== email) setRemote(null);
    else setRemote(mapRemote(buttons));
  }, [buttons, info?.currentUser, remoteLoading]);

  useEffect(() => {
    if (info && info.url && info.currentUser) {
      //:8086
      setSource(info.url);
    } else {
      setSource(undefined);
    }
  }, [info?.url, info?.currentUser]);

  useEffect(() => {
    if (info && info.url && info.currentUser && info.currentUser === email) {
      getRemoteButtons();
    }
  }, [info?.url, info?.currentUser]);

  const connectToCitrusView = () => {
    connect();
    updatePlayerConfig('light', false);
  };

  const disconnectFromCitrusView = () => {
    disconnect();
    updatePlayerConfig('light', true);
  };

  const hideRemote = (hidden: boolean) => {
    if (remote) setRemote(Object.assign(remote, { hidden: hidden }));
  };

  const onConnected = (playerEl: HTMLVideoElement) => {
    setConnected(true);
    setPlayerEl(playerEl);
  };

  const updatePlayerConfig = (
    config: keyof ReactPlayerProps | ReactPlayerProps,
    value?: any
  ) => {
    if (typeof config === 'object') setPlayerConfig(config);
    else setPlayerConfig((rpc) => Object.assign(rpc, { [config]: value }));
  };

  const updateStatistics = (
    statistic: keyof VideoPlayerStatistics | VideoPlayerStatistics,
    value?: any
  ) => {
    if (typeof statistic === 'object') setStatistics(statistic);
    else setStatistics((stats) => Object.assign(stats, { [statistic]: value }));
  };

  const resetSource = () => {
    setSource(undefined);
    setTimeout(() => setSource(info?.url ?? ''), 1000);
  };

  return (
    <CitrusViewContext.Provider
      value={{
        citrusViewDevice,
        connect: connectToCitrusView,
        connected,
        connectedUser: info?.currentUser,
        devices,
        disconnect: disconnectFromCitrusView,
        hasCitrusView: Boolean(info?.url),
        hideRemote,
        isMobile,
        jumpToLive,
        loading,
        onConnected,
        onVideoDrift: setJumpToLive,
        playerConfig,
        playerEl,
        reboot,
        rebooting,
        remote,
        remoteLoading,
        resetSource,
        sendCommand,
        setCitrusViewDevice,
        source,
        statistics,
        updatePlayerConfig,
        updateStatistics,
      }}
    >
      {children}
    </CitrusViewContext.Provider>
  );
};

export const useCitrusView = () => {
  const context = useContext(CitrusViewContext);

  if (context === undefined)
    throw new Error('useCitrusView must be used within an CitrusViewProvider');

  return context;
};

const mapRemote = (buttons: RemoteButton[]): Remote => ({
  buttons: buttons,
  hidden: false,
});

const useCitrusViewAccount = () => {
  const [devices, setDevices] = useState<Device[]>([]);
  const { makeRequest, response, loading } = useFetch(true);
  const operator = useOperator();

  useEffect(() => {
    getCitrusViewAccount();
  }, [operator.id]);

  useEffect(() => {
    if (response && !loading) setDevices(mapAccountDevices(response));
  }, [response, loading]);

  const getCitrusViewAccount = () => {
    makeRequest(
      `${BASE_URL_MAP[operator.type]}/${operator?.id}/Accounts/QASTN`,
      'get'
    );
  };

  const mapAccountDevices = (account: ApiResAccount | HosApiAccount) => {
    let devices: Device[] = [];
    const accountResponse = mapAccount(account, operator);
    if (operator.type === 'Hospitality')
      devices = (accountResponse.devices as HosAccountLocation[]).flatMap(
        (l) => l.devices
      );
    else devices = accountResponse.devices as Device[];

    return devices.filter((d) => d.model.type === 'MediaRoom Set-Top Box');
  };

  return { devices };
};
